/**
 * EAV.RDP.ANALYTICS, Type Curves: the curves service's answer laid out as LSEG's Excel add-in
 * (Refinitiv for Office 26.30.2) lays it out.
 *
 *   BaseCurvesServicesResponseProcessor (1570)     static headers per subtype, Output picks the
 *     getOutputFields (75285)                      columns (`*`, `+x`, `-x`, `?optional`, `!required`),
 *                                                  getFields lists the headers, errors, Bad response syntax!
 *   BaseDefinitionsResProcessor (76344)            zcdefinitions (93589), ccdefinitions (29822)
 *   BaseTriangulateDefinitionsResProcessor (91156) irtriangulatedefinitions (41727), cctriangulatedefinitions (24282)
 *   ZeroCouponResProcessor (84959)                 zccurves (curve points), zcconstituents (instruments)
 *   CrossCurrencyResProcessor (83890)              cccurves
 *   ForwardCurvesResProcessor (2113)               forwardcurves
 */

import { BaseResponseProcessor, ClientResponse, FieldValues, ResponseHeader, capitalize } from "../adfin/responseBase";
import { CurvesRequest } from "./request";

const SEP = ";"; // LSEG: ProcessorsConfig.defaultSeparator (27821)
const GETFIELDS = "getfields"; // LSEG: BaseCurvesServicesResponseProcessor.GETFIELDS_COLUMN

type Item = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
type CurvesData = { headers: ResponseHeader[]; data: unknown[][] | null };

const noCapitalize = (s: string) => (s ? s[0].toLowerCase() + s.slice(1) : ""); // LSEG: noCapitalize (44181)
const stripFlag = (name: string) => (name[0] === "?" || name[0] === "!" ? name.substring(1) : name); // LSEG: getModifiedHeader (63223)
const isDataItem = (e: unknown) => !!e && (e as Item).error === undefined; // LSEG: isCSResponseDataItem (51291)
const list = (v: unknown): Item[] | null => (Array.isArray(v) ? v : null);
const h = (name: string, type = "String"): ResponseHeader => ({ name, type });

// ---------------------------------------------------------------------------------------------
// LSEG: getOutputFields and its helpers (75285)

const REGULAR = 0;
const REQUIRED = 1;
const OPTIONAL = 2;
type OutField = { name: string; nameLC: string; type: number };
type Expose = { exposeAllColumns: boolean; exposeAlsoOptionalColumns: boolean; doNotExposeAllColumns: boolean; explicitExposeAllColumns: boolean; explicitIndex: number };

/** The columns for Output's names among the headers (`?` optional, `!` required). */
export function getOutputFields(requested: string[], headers: string[]): string[] {
  const shown = new Set<string>();
  const hidden = new Set<string>();
  let out: string[] = [];
  if (!requested?.length || (requested.length === 1 && requested[0] === "")) requested = ["*"];
  const fields: OutField[] = headers.map((raw) => {
    let type = REGULAR;
    let name = raw;
    if (name.startsWith("!")) (type = REQUIRED), (name = name.substring(1));
    else if (name.startsWith("?")) (type = OPTIONAL), (name = name.substring(1));
    return { name, nameLC: name.toLowerCase(), type };
  });
  const c: Expose = { exposeAllColumns: false, exposeAlsoOptionalColumns: false, doNotExposeAllColumns: false, explicitExposeAllColumns: false, explicitIndex: -1 };
  updateRequestedFields(c, requested, out, hidden, shown);
  if (c.doNotExposeAllColumns) c.exposeAllColumns = false;
  if (!c.exposeAllColumns) return reorderOutputFields(fields, out, c, hidden);
  out = [];
  fields.forEach((f) => {
    if (!validateSet(hidden, shown, f, c)) out.push(f.name);
  });
  return out;
}

function validateSet(hidden: Set<string>, shown: Set<string>, f: OutField, c: Expose): boolean {
  return isSetIncludes(hidden, f.nameLC) || (f.type === OPTIONAL && !c.exposeAlsoOptionalColumns && !shown.has(f.nameLC));
}

function reorderOutputFields(fields: OutField[], out: string[], c: Expose, hidden: Set<string>): string[] {
  let after = 0;
  fields.forEach((f) => {
    const at = out.findIndex((t) => {
      const lc = t.toLowerCase();
      return lc === f.nameLC || f.nameLC.startsWith(`${lc}.`);
    });
    if (at !== -1) {
      if (after < at + 1) after = at + 1;
    } else if ((c.explicitExposeAllColumns || f.type === REQUIRED) && !isSetIncludes(hidden, f.nameLC)) {
      if (c.explicitIndex !== -1) out.splice(c.explicitIndex++, 0, f.name);
      else out.splice(after++, 0, f.name);
    }
  });
  return out;
}

function updateRequestedFields(c: Expose, requested: string[], out: string[], hidden: Set<string>, shown: Set<string>): void {
  requested.forEach((raw) => {
    let t = raw;
    if (t === "") {
      out.push(t);
      return;
    }
    if (t === "+*" || t === "*+" || t === "+") (t = "*"), (c.exposeAlsoOptionalColumns = true);
    if (t === "*") {
      if (!c.exposeAllColumns) (c.exposeAllColumns = true), (c.explicitExposeAllColumns = true), (c.explicitIndex = out.length);
      return;
    }
    let lc = t.toLowerCase();
    let hide = false;
    switch (t[0]) {
      case "+":
        (c.exposeAllColumns = true), (t = t.substring(1)), (lc = lc.substring(1));
        break;
      case "-":
        (c.exposeAllColumns = true), (hide = true), (t = t.substring(1)), (lc = lc.substring(1));
        break;
      default:
        c.doNotExposeAllColumns = true;
    }
    if (hide) hidden.add(lc), shown.delete(lc);
    else {
      hidden.delete(lc);
      if (!shown.has(lc)) shown.add(lc), out.push(t);
    }
  });
}

function isSetIncludes(set: Set<string>, name: string): boolean {
  if (!name.includes(".")) return set.has(name);
  for (const r of set) if (name === r || name.startsWith(`${r}.`)) return true;
  return false;
}

// ---------------------------------------------------------------------------------------------
// The processors

/** One processor for every curves subtype: the base's flow, the subtype's table from the answer. */
// LSEG: BaseCurvesServicesResponseProcessor (1570) and its subclasses
export class CurvesResProcessor extends BaseResponseProcessor {
  constructor(private readonly req: CurvesRequest) {
    super();
  }

  // LSEG: BaseCurvesServicesResponseProcessor_1570.response
  response(e: Item): ClientResponse {
    const options = this.req.options;
    const [option] = this.getDisplayOptions(options);
    const A = this.getDataFromResponse(e);
    const fields = this.getFields(A);
    let values: FieldValues;
    if (fields[0].toLowerCase() === GETFIELDS) values = this.getFieldsDisplayData(A);
    else {
      const error = this.getErrorResponseIfHasAny(e, A);
      if (error) return error;
      values = this.getDisplayData(A, fields);
    }
    const laid = this.transposeData(this.rhChFormat(values, option), options);
    return this.removeEmptyRows(this.clientFormat(laid));
  }

  // LSEG: BaseCurvesServicesResponseProcessor_1570.getErrorResponseIfHasAny
  getErrorResponseIfHasAny(e: Item, A: CurvesData): ClientResponse | undefined {
    if (e?.error !== undefined) return this.getCSErrorResponse(e);
    if (e?.data && (e.data as Item[])[0]?.error !== undefined) return this.getCSErrorResponse((e.data as Item[])[0]);
    if (A?.data) return undefined;
    return this.getErrorResponse({ error: { message: "Bad response syntax!", status: "", errors: [] } });
  }

  // LSEG: BaseCurvesServicesResponseProcessor_1570.getCSErrorResponse (no apiError: that is LSEG's requestor's)
  getCSErrorResponse(e: Item): ClientResponse {
    return this.getErrorResponse({ error: { errors: [], status: "", ...e.error } });
  }

  /** Output split; `getFields` alone stays, else the columns getOutputFields picks among the headers. */
  // LSEG: BaseCurvesServicesResponseProcessor_1570.getFields
  getFields(A: CurvesData): string[] {
    const requested = this.req.output.split(SEP);
    if (requested.length === 1 && requested[0].toLowerCase() === GETFIELDS) return requested;
    const names: string[] = [];
    A?.headers?.forEach((x) => {
      if (x?.name) names.push(x.name);
    });
    return getOutputFields(requested, names);
  }

  // LSEG: BaseCurvesServicesResponseProcessor_1570.getFieldsDisplayData
  getFieldsDisplayData(A: CurvesData): FieldValues {
    return {
      requestIDs: A.headers.map((x) => x.name || ""),
      fields: ["type", "name"],
      valueSets: A.headers.map((x) => [x.type, noCapitalize(stripFlag(x.name || ""))]),
    };
  }

  // LSEG: BaseCurvesServicesResponseProcessor_1570.getDisplayData, getRequestIds, getValueSets
  getDisplayData(A: CurvesData, requested: string[]): FieldValues {
    const fields = this.getFieldsNames(requested, A);
    const lower = A.headers.map((x) => stripFlag(x.name).toLowerCase());
    const indexes = A.headers.length && fields.length ? fields.map((f) => lower.findIndex((n) => n === f.toLowerCase())) : [];
    return {
      requestIDs: A.data ? Array(A.data.length).fill(null) : [],
      fields,
      valueSets: (A.data ?? []).map((row) => indexes.map((i) => this.getValueIfEmpty(row[i]))),
    };
  }

  /** Each requested name's headers (a name also takes its `name.x` headers, but not an optional one), else the name. */
  // LSEG: BaseCurvesServicesResponseProcessor_1570.getFieldsNames, BaseResponseProcessor_63223.getHeaderResponse
  getFieldsNames(requested: string[], A: CurvesData): string[] {
    if (!A?.headers?.length || !requested?.length) return [];
    const out: string[] = [];
    requested.forEach((e) => {
      const r = e.toLowerCase();
      let found = false;
      A.headers.forEach((x) => {
        const name = stripFlag(x.name);
        const lc = name.toLowerCase();
        if (lc === r || (lc.startsWith(`${r}.`) && x.name[0] !== "?")) (found = true), out.push(capitalize(name));
      });
      if (!found) out.push(capitalize(e));
    });
    return out;
  }

  /** The subtype's table: static headers, rows from the answer (null when it has no `data` list). */
  getDataFromResponse(e: Item): CurvesData {
    switch (this.req.curves) {
      case "zcdefinitions":
        return definitions(e, ZC_DEFINITION_HEADERS, (d, tag) => [
          tag, d.currency, d.mainConstituentAssetClass, d.riskType, d.indexName, d.source, d.name,
          d.firstHistoricalAvailabilityDate, d.id, d.availableTenors.join(","), d.availableDiscountingTenors.join(","),
        ]);
      case "ccdefinitions":
        return definitions(e, CC_DEFINITION_HEADERS, (d, tag) => [
          tag, d.baseCurrency, d.baseIndexName, d.quotedCurrency, d.quotedIndexName, d.name, d.source, d.id,
          d.firstHistoricalAvailabilityDate, d.mainConstituentAssetClass, d.isNonDeliverable, d.isFallbackForFxCurveDefinition,
        ]);
      case "irtriangulatedefinitions":
        return triangulate(e, IR_TRIANGULATE_HEADERS, (d, type, tag) => [
          type, tag, d.currency, d.mainConstituentAssetClass, d.riskType, d.indexName, d.source, d.name,
          d.firstHistoricalAvailabilityDate, d.id, d.availableTenors.join(","), d.availableDiscountingTenors.join(","),
        ]);
      case "cctriangulatedefinitions":
        return triangulate(e, CC_TRIANGULATE_HEADERS, (d, type) => [
          type, d.baseCurrency, d.baseIndexName, d.quotedCurrency, d.quotedIndexName, d.name, d.source, d.id,
          d.mainConstituentAssetClass, d.isNonDeliverable, d.isFallbackForFxCurveDefinition,
        ]);
      case "zccurves":
        return zcCurves(e)[0];
      case "zcconstituents":
        return zcConstituents(e, zcCurves(e)[1]);
      case "cccurves":
        return ccCurves(e);
      default:
        return forwardCurves(e);
    }
  }
}

// LSEG: ZeroCouponDefinitionResProcessor_93589.getHeaders
const ZC_DEFINITION_HEADERS = [
  h("?curveTag"), h("currency"), h("mainConstituentAssetClass"), h("riskType"), h("indexName"), h("source"), h("name"),
  h("firstHistoricalAvailabilityDate", "Date"), h("?id"), h("availableTenors"), h("availableDiscountingTenors"),
];
// LSEG: CrossCurrencyDefinitionsResProcessor_29822.getHeaders
const CC_DEFINITION_HEADERS = [
  h("?curveTag"), h("baseCurrency"), h("baseIndexName"), h("quotedCurrency"), h("quotedIndexName"), h("name"), h("source"), h("?id"),
  h("firstHistoricalAvailabilityDate", "Date"), h("mainConstituentAssetClass"), h("isNonDeliverable", "Boolean"),
  h("isFallbackForFxCurveDefinition", "Boolean"),
];
// LSEG: InterestRateTriangulateDefinitionsResProcessor_41727.getHeaders
const IR_TRIANGULATE_HEADERS = [h("!type"), ...ZC_DEFINITION_HEADERS];
// LSEG: CrossCurrencyTriangulateDefinitionsResProcessor_24282.getHeaders
const CC_TRIANGULATE_HEADERS = [
  h("!type"), h("baseCurrency"), h("baseIndexName"), h("quotedCurrency"), h("quotedIndexName"), h("name"), h("source"), h("?id"),
  h("mainConstituentAssetClass"), h("isNonDeliverable", "Boolean"), h("isFallbackForFxCurveDefinition", "Boolean"),
];

/** One row per `data[].curveDefinitions[]`. */
// LSEG: BaseDefinitionsResProcessor_76344.getDataFromResponse
function definitions(e: Item, headers: ResponseHeader[], row: (d: Item, tag: unknown) => unknown[]): CurvesData {
  const items = list(e?.data);
  const data: unknown[][] | null = items ? [] : null;
  items?.forEach((item) => {
    if (!isDataItem(item) || !item.curveDefinitions) return;
    (item.curveDefinitions as Item[]).forEach((d) => data!.push(row(d, item.curveTag)));
  });
  return { headers, data };
}

/** Direct definitions, then per indirect one: its pivot, its curve and cross-currency definitions. */
// LSEG: BaseTriangulateDefinitionsResProcessor_91156.getDataFromResponse
function triangulate(e: Item, headers: ResponseHeader[], row: (d: Item, type: string, tag: unknown) => unknown[]): CurvesData {
  const items = list(e?.data);
  const data: unknown[][] | null = items ? [] : null;
  items?.forEach((item) => {
    if (!isDataItem(item)) return;
    const direct = item.directCurveDefinitions as Item[] | undefined;
    const indirect = item.indirectCurveDefinitions as Item[] | undefined;
    if (!direct && !indirect) return;
    direct?.forEach((d) => data!.push(row(d, "Direct", item.curveTag)));
    indirect?.forEach((d) => {
      if (d.pivotCurveDefinition) data!.push(row(d.pivotCurveDefinition, "Pivot", item.curveTag));
      (d.curveDefinitions as Item[] | undefined)?.forEach((x) => data!.push(row(x, "Indirect", item.curveTag)));
      (d.crossCurrencyDefinitions as Item[] | undefined)?.forEach((x) => data!.push(row(x, "Indirect", item.curveTag)));
    });
  });
  return { headers, data };
}

const instrumentTenorKey = (i: number, code: unknown, tenor: unknown) =>
  code && tenor ? `IDX:${i}-INSTR(${String(code).toLowerCase()})-TENOR(${String(tenor).toLowerCase()})` : null;

/**
 * One row per curve point of each index tenor (the `discountingTenor` column holds that tenor),
 * then its instruments' codes, one column each; and each instrument's dates, for zcconstituents.
 */
// LSEG: ZeroCouponResProcessor_84959.getChartDataSetFromResponse, setDataItems
function zcCurves(e: Item): [CurvesData, Map<string, { startDate: unknown; endDate: unknown }>] {
  const headers = [
    h("currency"), h("indexName"), h("discountingTenor"), h("isDiscountCurve", "Boolean"), h("tenor"), h("startDate", "Date"),
    h("endDate", "Date"), h("discountFactor", "Float"), h("ratePercent", "Float"),
  ];
  const dates = new Map<string, { startDate: unknown; endDate: unknown }>();
  const items = list(e?.data);
  const rows: unknown[][] | null = items ? [] : null;
  let most = 1;
  items?.forEach((item, idx) => {
    if (!isDataItem(item)) return;
    const currency = item?.curveDefinition?.currency ?? "";
    const indexName = item?.curveDefinition?.indexName ?? "";
    const curves = item.curves;
    if (!curves) return;
    (item.curveDefinition.indexTenors as string[]).forEach((tenor) => {
      const curve = curves[tenor];
      (curve.curvePoints as Item[]).forEach((p) => {
        const codes = (p?.instruments as Item[] | undefined)?.map((x) => x.instrumentCode) ?? [];
        if (codes.length > most) most = codes.length;
        codes.forEach((code) => {
          const key = instrumentTenorKey(idx, code, p.tenor);
          if (key) dates.set(key, { startDate: p.startDate, endDate: p.endDate });
        });
        rows!.push([currency, indexName, tenor, curve.isDiscountCurve, p.tenor, p.startDate, p.endDate, p.discountFactor, p.ratePercent, ...codes]);
      });
    });
  });
  headers.push(...Array.from({ length: most }, (_, i) => h(`instrumentCode${i + 1}`)));
  const width = headers.length;
  return [{ headers, data: rows?.map((r) => [...r, ...Array(width - r.length).fill("")]) ?? null }, dates];
}

/** One row per constituent instrument without a status message, kinds in LSEG's order. */
// LSEG: ZeroCouponResProcessor_84959.getConstituentsDataSetFromResponse, getInstrumentsRow
function zcConstituents(e: Item, dates: Map<string, { startDate: unknown; endDate: unknown }>): CurvesData {
  const kinds = ["deposits", "fras", "futures", "interestRateSwaps", "overnightIndexSwaps", "tenorBasisSwaps"];
  const items = list(e?.data);
  const data: unknown[][] | null = items ? [] : null;
  items?.forEach((item, idx) => {
    if (!isDataItem(item)) return;
    const currency = item?.curveDefinition?.currency ?? "";
    const indexName = item?.curveDefinition?.indexName ?? "";
    const discountingTenor = item?.curveDefinition?.discountingTenor ?? "";
    const instruments = item?.constituents?.interestRateInstruments[currency];
    if (!instruments) return;
    kinds.forEach((kind) => {
      (instruments[kind] as Item[] | undefined)?.forEach((x) => {
        if (x.statusMessage) return;
        const def = x.instrumentDefinition;
        const { startDate, endDate } = dates.get(instrumentTenorKey(idx, def.instrumentCode, def.tenor) as string) ?? { startDate: "", endDate: "" };
        data!.push([
          currency, indexName, discountingTenor, kind, def.instrumentCode, def.template ?? "", def.tenor ?? "", startDate, endDate,
          x.fields.bid?.value ?? null, x.fields.ask?.value ?? null, x?.basis?.join(",") ?? "",
        ]);
      });
    });
  });
  return {
    headers: [
      h("currency"), h("indexName"), h("discountingTenor"), h("constituents"), h("instrumentCode"), h("template"), h("tenor"),
      h("startDate", "Date"), h("endDate", "Date"), h("field.bid"), h("field.ask"), h("field.basis"),
    ],
    data,
  };
}

/** Every curve point of every answer item, one after the other. */
// LSEG: CrossCurrencyResProcessor_83890.getDataFromResponse
function ccCurves(e: Item): CurvesData {
  const items = list(e?.data);
  const data: unknown[][] | null = items ? [] : null;
  items?.forEach((item) => {
    if (!isDataItem(item)) return;
    (item?.curve?.curvePoints as Item[] | undefined)?.forEach((p) => {
      data!.push([
        p?.tenor ?? "", p?.startDate ?? "", p?.endDate ?? "", p?.swapPoint?.bid ?? "", p?.swapPoint?.ask ?? "", p?.swapPoint?.mid ?? "",
        p?.swapPoint?.status ?? "", p?.outright?.bid ?? "", p?.outright?.ask ?? "", p?.outright?.mid ?? "", p?.outright?.status ?? "",
      ]);
    });
  });
  return {
    headers: [
      h("tenor"), h("startDate", "Date"), h("endDate", "Date"), h("swapPoint.bid", "Float"), h("swapPoint.ask", "Float"),
      h("swapPoint.mid", "Float"), h("?swapPoint.status"), h("outright.bid", "Float"), h("outright.ask", "Float"),
      h("outright.mid", "Float"), h("?outright.status"),
    ],
    data,
  };
}

/** Per forward curve: an error row (message in the hidden errorMessage column), then its points. */
// LSEG: ForwardCurvesResProcessor_2113.getDataFromResponse, getErrorIfHasAny, getCurvePoint
function forwardCurves(e: Item): CurvesData {
  const items = list(e?.data);
  const data: unknown[][] | null = items ? [] : null;
  items?.forEach((item) => {
    if (!isDataItem(item) || !item.forwardCurves) return;
    const head = (c: Item) => [
      item?.curveDefinition?.currency ?? "", item?.curveDefinition?.indexName ?? "", item?.curveDefinition?.discountingTenor ?? "",
      c.forwardCurveTag, c.forwardStart, c.indexTenor,
    ];
    (item.forwardCurves as Item[]).forEach((c) => {
      if (c.error) {
        const err = c.error;
        const message = err?.message ?? `Error returned, code:${err?.code ?? "???"}, id:${err?.id ?? "???"}`;
        data!.push([...head(c), "", "", "", "", "", message]);
      }
      (c?.curvePoints as Item[] | undefined)?.forEach((p) =>
        data!.push([...head(c), p.startDate, p.endDate, p.discountFactor, p.ratePercent, p.tenor, ""]),
      );
    });
  });
  return {
    headers: [
      h("currency"), h("indexName"), h("discountingTenor"), h("?forwardCurveTag"), h("forwardStart", "Date"), h("indexTenor"),
      h("startDate", "Date"), h("endDate", "Date"), h("discountFactor", "Float"), h("ratePercent", "Float"), h("tenor"), h("?errorMessage"),
    ],
    data,
  };
}
